import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createClosure, deleteClosure, fetchClosures, updateClosure } from "../services/branchService";
import NotifyCustomersLink from "./NotifyCustomersLink";

/**
 * The notice a closure row prefills: closed all day → a temporary closure of
 * the whole date; special hours → an hours change for that one date, so only
 * bookings outside the special hours are notified.
 */
const closureNotice = (branchId, closure) =>
  closure.is_closed
    ? {
        type: "branch_temporary_closure",
        scope: { branch_ids: [branchId], starts_at: `${closure.date} 00:00`, ends_at: `${closure.date} 23:59` },
      }
    : {
        type: "opening_hours_change",
        scope: {
          branch_id: branchId,
          date_from: closure.date,
          date_to: closure.date,
          hours: [
            {
              day: new Date(`${closure.date}T12:00:00Z`).getUTCDay(),
              opens_at: (closure.opens_at ?? "").slice(0, 5),
              closes_at: (closure.closes_at ?? "").slice(0, 5),
            },
          ],
        },
      };

const EMPTY = { date: "", isClosed: true, opensAt: "10:00", closesAt: "16:00", reason: "" };

/**
 * The dates this branch does not follow its weekly schedule on: a holiday it is
 * shut for, or a day it opens on different hours.
 *
 * Past dates are hidden unless asked for — a branch accumulates years of them
 * and only the ones still to come change what anybody can book.
 */
export default function BranchClosures({ branchId }) {
  const { t } = useTranslation();

  const [closures, setClosures] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setClosures(await fetchClosures(branchId, { past: showPast }));
    } catch (err) {
      setError(apiErrorMessage(err, t("closures.loadFailed")));
      setClosures([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, showPast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
    setErrors({});
  };

  const edit = (closure) => {
    setEditingId(closure.id);
    setErrors({});
    setForm({
      date: closure.date,
      isClosed: closure.is_closed,
      opensAt: closure.opens_at ?? "10:00",
      closesAt: closure.closes_at ?? "16:00",
      reason: closure.reason ?? "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setError("");

    try {
      if (editingId) await updateClosure(branchId, editingId, form);
      else await createClosure(branchId, form);

      reset();
      load();
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("closures.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (closure) => {
    setError("");
    try {
      await deleteClosure(branchId, closure.id);
      if (editingId === closure.id) reset();
      load();
    } catch (err) {
      setError(apiErrorMessage(err, t("closures.deleteFailed")));
    }
  };

  const fieldError = (field) => errors[field]?.[0];

  return (
    <div className="card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{t("closures.title")}</h2>
          <p className="text-xs text-ink-2">{t("closures.subtitle")}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-2">
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong text-accent-text"
          />
          {t("closures.showPast")}
        </label>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {loading ? (
        <p className="py-4 text-sm text-ink-2">{t("common.loading")}</p>
      ) : closures.length === 0 ? (
        <p className="py-4 text-sm text-ink-2">{t("closures.empty")}</p>
      ) : (
        <ul className="mb-4 divide-y divide-line">
          {closures.map((closure) => (
            <li key={closure.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div>
                <p className="text-sm font-medium" dir="ltr">
                  {closure.date}
                </p>
                <p className="text-xs text-ink-2">
                  {closure.is_closed
                    ? t("closures.closedAllDay")
                    : `${closure.opens_at} – ${closure.closes_at}`}
                  {closure.reason ? ` · ${closure.reason}` : ""}
                </p>
                {/* Tell the customers booked on this date (only the ones it affects). */}
                <NotifyCustomersLink
                  className="mt-0.5"
                  {...closureNotice(branchId, closure)}
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => edit(closure)}
                  className="text-sm font-semibold text-accent-text hover:underline"
                >
                  {t("common.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => remove(closure)}
                  className="text-sm font-semibold text-danger-text hover:underline"
                >
                  {t("common.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="grid gap-3 border-t border-line pt-4 md:grid-cols-2" noValidate>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-2 md:col-span-2">
          {editingId ? t("closures.editTitle") : t("closures.addTitle")}
        </p>

        <div>
          <label className="field-label" htmlFor="closure-date">
            {t("closures.date")} <span className="text-danger-text">*</span>
          </label>
          <input
            id="closure-date"
            type="date"
            dir="ltr"
            className="field-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          {fieldError("date") && <p className="field-error">{fieldError("date")}</p>}
        </div>

        <div>
          <label className="field-label" htmlFor="closure-mode">
            {t("closures.mode")}
          </label>
          <select
            id="closure-mode"
            className="field-input"
            value={form.isClosed ? "closed" : "hours"}
            onChange={(e) => setForm({ ...form, isClosed: e.target.value === "closed" })}
          >
            <option value="closed">{t("closures.modes.closed")}</option>
            <option value="hours">{t("closures.modes.hours")}</option>
          </select>
        </div>

        {/* Hours are only meaningful when the branch is open that day. */}
        {!form.isClosed && (
          <>
            <div>
              <label className="field-label" htmlFor="closure-opens">
                {t("closures.opensAt")}
              </label>
              <input
                id="closure-opens"
                type="time"
                dir="ltr"
                className="field-input"
                value={form.opensAt}
                onChange={(e) => setForm({ ...form, opensAt: e.target.value })}
              />
              {fieldError("opens_at") && <p className="field-error">{fieldError("opens_at")}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="closure-closes">
                {t("closures.closesAt")}
              </label>
              <input
                id="closure-closes"
                type="time"
                dir="ltr"
                className="field-input"
                value={form.closesAt}
                onChange={(e) => setForm({ ...form, closesAt: e.target.value })}
              />
              {fieldError("closes_at") && <p className="field-error">{fieldError("closes_at")}</p>}
            </div>
          </>
        )}

        <div className="md:col-span-2">
          <label className="field-label" htmlFor="closure-reason">
            {t("closures.reason")}
          </label>
          <input
            id="closure-reason"
            className="field-input"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
          {fieldError("reason") && <p className="field-error">{fieldError("reason")}</p>}
        </div>

        <div className="flex gap-2 md:col-span-2">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? t("common.loading") : editingId ? t("common.save") : t("closures.add")}
          </button>
          {editingId && (
            <button type="button" className="btn-secondary" onClick={reset} disabled={saving}>
              {t("common.cancel")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
